import { Component, Input, Output, EventEmitter, ViewEncapsulation, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  MAIH_ANALYSIS_TYPE,
  MAIH_SECTION_NAME,
  MAIH_SECTION_TYPE,
  MaihAgentContext,
  MaihEvent,
} from './emma-maih-agent.types';
import { localizeAiAnalysis } from '@emma-pages/legacy/acquisition/apptracker/apptracker.i18n';
import { DATE_FORMATS, DateRange } from 'emma-common-ts';
import { AiService, AnalysisResponse, CreateAnalysisRequest } from '@emma-services/ai.service';
import { CurrentAppService } from '@emma-services/current-app.service';
import { AIAnalysisService } from '@emma-services/ai-analysis.service';
import { DateChangeService } from '@platform/services/date-change.service';
import { CurrentAiService } from '@emma-services/ai-current-session.service';
import {
  GTM_EVENT_ACTION,
  GTM_EVENT_SECTION,
  GtmDataLayerService,
} from '@emma-services/gtm-data-layer.service';
import { UserService } from '@emma-services/user.service';
import { combineLatest, filter, takeUntil } from 'rxjs';
import { OnUnsubscribe } from '@platform/helpers/on-unsubscribe.class';
import { getCurrentLanguage } from '@platform/services/locale.service';
import dayjs from 'dayjs';

@Component({
    selector: 'emma-maih-agent',
    templateUrl: './emma-maih-agent.component.html',
    styleUrls: ['./emma-maih-agent.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('slideInUp', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateY(30px)',
                }),
                animate('1500ms ease-out', style({
                    opacity: 1,
                    transform: 'translateY(0)',
                })),
            ]),
        ]),
    ],
    standalone: false
})
export class EMMAMaihAgentComponent extends OnUnsubscribe implements OnInit {
  @Input() title = '';
  @Input() description = '';
  @Input() buttonText = localizeAiAnalysis.BUTTON_TEXT;
  @Input() analyzingButtonText = localizeAiAnalysis.ANALYZING;
  @Input() loading = new Set<string>();
  @Input() gtmEventSection: GTM_EVENT_SECTION = GTM_EVENT_SECTION.APPTRACKER_CAMPAIGNS;
  @Input() analysisSectionName: MAIH_SECTION_NAME = MAIH_SECTION_NAME.APPTRACKER;
  @Input() analysisSectionType: MAIH_SECTION_TYPE = MAIH_SECTION_TYPE.CAMPAIGNS;
  @Input() analysisType: MAIH_ANALYSIS_TYPE = MAIH_ANALYSIS_TYPE.OVERVIEW;
  @Input() selectedEvents: MaihEvent[] = [];

  @Output() createAnalysisClick = new EventEmitter<void>();
  @Output() closeError = new EventEmitter<void>();

  private currentPickedDate: DateRange | null = null;
  private accessToken: string | null = null;
  private apiKey: string | null = null;
  private userId: number | null = null;
  private app: string | null = null;

  isAnalyzing = false;
  isShowingChat = false;
  chatContextData: MaihAgentContext | null = null;
  hasError = false;
  errorMessage = '';

  constructor(
    private readonly aiService: AiService,
    private readonly currentAppService: CurrentAppService,
    private readonly aiAnalysisService: AIAnalysisService,
    private readonly dateService: DateChangeService,
    private readonly currentAiService: CurrentAiService,
    private readonly gtmDataLayerService: GtmDataLayerService,
    private readonly userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    const currentUserString = sessionStorage.getItem('currentUser');
    if (currentUserString) {
      const currentUser = JSON.parse(currentUserString);
      this.accessToken = currentUser.access_token;
    }

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.userId = user?.id ?? null;
      });

    combineLatest([this.currentAppService.$currentApp, this.dateService.getChangeEmitter()])
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(([app]) => !!app?.appConfig.apiKey && !!this.accessToken)
      )
      .subscribe(([app, dateRange]) => {
        this.apiKey = app?.appConfig.apiKey ?? null;
        this.app = app?.appConfig.name ?? null;
        this.fetchAIAnalysis(dateRange.startDay, dateRange.endDay);
        this.currentPickedDate = dateRange;
      });

    this.aiAnalysisService.analysis$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((analysis) => {
      if (analysis && analysis.type === this.analysisSectionType) {
        this.chatContextData = {
          type: 'maih-analysis',
          data: analysis.text,
          section: this.analysisSectionName,
        };
        this.isShowingChat = true;
      } else if (!analysis) {
        this.isShowingChat = false;
        this.chatContextData = null;
      }
    });

    this.aiAnalysisService.error$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((error) => {
      if (error) {
        this.isShowingChat = false;
      }
    });
  }

  async fetchAIAnalysis(startDay: string, endDay: string): Promise<void> {
    if (!this.accessToken || !this.apiKey) {
      this.aiAnalysisService.setError('Missing access token or API key');
      return;
    }

    this.loading.add('fetchAIAnalysis');

    try {
      const analysesResponse = await this.aiService.listAnalyses(
        this.accessToken,
        this.apiKey,
        this.analysisSectionName
      );

      if (!analysesResponse.analyses || analysesResponse.analyses.length === 0) {
        this.aiAnalysisService.setError('There arent availables analyses');
        return;
      }

      const analysesInDateRange = analysesResponse.analyses.filter((analysis) => {
        const rawStart = analysis.start_date;
        const rawEnd = analysis.end_date;
        if (!rawStart || !rawEnd) return false;

        const analysisStartDate = rawStart.split('T')[0];
        const analysisEndDate = rawEnd.split('T')[0];

        return (
          analysisStartDate === startDay &&
          analysisEndDate === endDay &&
          analysis.language === getCurrentLanguage()
        );
      });

      if (analysesInDateRange.length === 0) {
        this.aiAnalysisService.clearAnalysis();
        this.isShowingChat = false;
        return;
      }

      const mostRecentAnalysis = analysesInDateRange.reduce(
        (latest, current) => (new Date(current.created_at) > new Date(latest.created_at) ? current : latest),
        analysesInDateRange[0]
      );

      const analysisDetails = await this.aiService.getAnalysis(
        this.accessToken,
        this.apiKey,
        this.analysisSectionName,
        mostRecentAnalysis.analysis_id
      );

      if (!analysisDetails.analysis_content) {
        this.aiAnalysisService.setError('Analysis has no content');
        return;
      }

      this.currentAiService.updateAnalysisId(this.analysisSectionName, analysisDetails.analysis_id);
      this.aiAnalysisService.setAnalysis(analysisDetails.analysis_content, this.analysisSectionType);
    } catch {
      this.aiAnalysisService.setError('Error fetching AI Analysis');
    } finally {
      this.loading.delete('fetchAIAnalysis');
    }
  }
  // MAIH Channels
  async onMaihAgentClick(): Promise<void> {
    this.isAnalyzing = true;
    this.hasError = false;

    try {
      const analysisContent = await this.generateAnalysis();
      this.chatContextData = {
        type: 'maih-analysis',
        data: analysisContent,
        section: this.analysisSectionName,
      };
      this.isShowingChat = true;
    } catch {
      this.hasError = true;
      this.errorMessage = $localize`Ha ocurrido un error al generar el análisis. Por favor, inténtelo nuevamente en unos momentos. Si el problema persiste, no dude en contactarnos.`;
    } finally {
      this.isAnalyzing = false;
    }
    this.createAnalysisClick.emit();
  }

  async generateAnalysis(): Promise<string> {
    if (!this.accessToken) {
      throw new Error('Access token not available');
    }

    if (!this.apiKey) {
      throw new Error('API key not available');
    }

    try {
      const startDate =
        this.currentPickedDate?.startDay ?? dayjs().subtract(30, 'day').format(DATE_FORMATS.API);
      const endDate = this.currentPickedDate?.endDay ?? dayjs().format(DATE_FORMATS.API);

      const request: CreateAnalysisRequest = {
        section: this.analysisSectionName,
        start_date: startDate,
        end_date: endDate,
        analysis_type: this.analysisType,
        language: getCurrentLanguage(),
        custom_events: this.selectedEvents,
        additional_params: {},
      };

      const response: AnalysisResponse = await this.aiService.createAnalysis(
        this.accessToken,
        this.apiKey,
        this.analysisSectionName,
        request
      );

      this.gtmDataLayerService.logEvent(GTM_EVENT_ACTION.ANALYSIS, {
        app: this.app,
        section: this.gtmEventSection,
        user_id: this.userId,
        date: `${startDate} - ${endDate}`,
      });

      this.currentAiService.updateAnalysisId(this.analysisSectionName, response.analysis_id);

      return response.analysis_content || 'No se pudo generar el análisis';
    } catch {
      throw new Error('Error generating analysis');
    }
  }

  onMaihErrorClose(): void {
    this.hasError = false;
    this.errorMessage = '';
    this.closeError.emit();
  }
}
